/**
 * Typed, centralized access to build-time env (injected by rspack DefinePlugin).
 * Never read import.meta.env.APP_* directly elsewhere — go through `env`.
 */
export const env = {
  appEnv: import.meta.env.APP_ENV || 'development',
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  authUrl: import.meta.env.APP_AUTH_URL,
  // DataCentral defaults to v4 (APP_DATACENTRAL_API_URL points at /api/v4).
  dataCentralApiUrl: import.meta.env.APP_DATACENTRAL_API_URL,
  serviceApiUrl: import.meta.env.APP_SERVICE_API_URL,
  serviceApiV3Url: import.meta.env.APP_SERVICE_API_V3_URL,
  // margarita-foundation (identity, organization, teams, projects, IAM) — the
  // foundation half split out of @mgmt. Base includes /api/v1. Optional: when
  // unset the foundation clients fall back to @mgmt v3, which still serves the
  // same endpoints, so a build without it behaves exactly as before.
  foundationApiUrl: import.meta.env.APP_FOUNDATION_API_URL,
  // Full URL of margarita-foundation `GET /system/maintenance` (public). Empty = no maintenance gate.
  // Independent of APP_FOUNDATION_API_URL: the gate works before any API is cut over to foundation.
  maintenanceUrl: import.meta.env.APP_MAINTENANCE_URL || '',
  // FastChannel (FAST playout) — service_uuid-scoped v2 API on a dedicated host.
  // `fastChannelApiUrl` is the control plane (…/api); `fastChannelUrl` is the
  // streaming/health host used for channel health checks.
  fastChannelApiUrl: import.meta.env.APP_FASTC_API_URL,
  fastChannelUrl: import.meta.env.APP_FASTC_URL,

  // Tiger (managed SSL / cert inventory) — @tiger controlplane, called directly
  // (same shape as fastChannel). Base includes /api/v1. JWT-scoped; tenant is
  // implicit from the org claim (no tenant/serviceId in any path). This is the
  // single cert inventory: list/upload/issue/renew/revoke/bundle.
  tigerApiUrl: import.meta.env.APP_TIGER_API_URL,

  // Healthcheck & Alerting (monitoring) — dedicated Go service. Base already
  // includes /api/v1; JWT-scoped, watchpoints filtered by project_id.
  healthcheckApiUrl: import.meta.env.APP_HEALTHCHECK_API_URL,

  // DRM control plane (sig-control) — multi-tenant Go service called directly,
  // JWT-scoped. Base already includes /api (streams/provision/usage). Tenant is
  // implicit from the JWT org claim — no tenant/serviceId in any path.
  controlApiUrl: import.meta.env.APP_CONTROL_API_URL,

  // AES/DRM gateway (zone endpoint) — the public host players/customer backends
  // call for /drm/signed + /drm/keyfile. A separate service from the control
  // plane; the console only renders its example URLs and a health pill.
  aesGatewayHost: import.meta.env.APP_AES_API_URL,

  // NOTE: the AES gateway ops surface (/logz, /metricsz, /usagez, /healthz) is
  // reached through the @mgmt control plane (serviceApiV3), which holds the
  // gateway ADMIN_API_KEY server-side. No browser-side AES URL/key here.

  keycloak: {
    url: import.meta.env.APP_KEYCLOAK_URL || 'https://id.vnetwork.dev',
    realm: import.meta.env.APP_KEYCLOAK_REALM || 'partner',
    clientId: import.meta.env.APP_KEYCLOAK_CLIENT_ID || 'partner-console',
  },

  // Google standalone sign-in (direct Google Identity Services, NOT via Keycloak).
  // Public OAuth Web client id; ships in the bundle by design. Empty = the
  // standalone Google button stays hidden (see GoogleSignInButton).
  google: {
    clientId: import.meta.env.APP_GOOGLE_CLIENT_ID || '',
  },

  // Firebase Analytics (web) = Google Analytics 4 under the hood. The web app
  // config is public by design (it ships in the client bundle); the values come
  // from Firebase console → Project settings → Your apps → Web app. Analytics
  // stays a no-op unless `measurementId` (G-XXXX) is present — see `analytics.ts`.
  firebase: {
    apiKey: import.meta.env.APP_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.APP_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.APP_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.APP_FIREBASE_MEASUREMENT_ID || '',
  },

  // VMware WebMKS console tunnel — the base URL of the vSphere HTML5 web-console
  // proxy (same var as APP_SSH_TUNNEL_URL in @console). The console iframe is built
  // as `${webConsoleTunnelUrl}/ui/webconsole.html?vmId=...&session=...`.
  webConsoleTunnelUrl: import.meta.env.APP_SSH_TUNNEL_URL || '',

  // Product documentation (Docusaurus, docs.vnetwork.dev). Served as a static
  // SSG site with open CORS, so the in-app Documentation panel fetches a page's
  // rendered <article> and re-renders it natively with our tokens.
  docsUrl: import.meta.env.APP_DOCS_URL || 'https://docs.vnetwork.dev',

  // Ask AI / live chat. Bot replies come from the N8N webhook; when the bot
  // escalates, the panel connects to the VChat socket for a human agent.
  // Defaults mirror the @livechat widget so this works without extra config.
  chat: {
    chatbotUrl:
      import.meta.env.APP_CHATBOT_URL || 'https://n8n.vnetwork.dev/webhook/chatbot',
    socketUrl:
      import.meta.env.APP_CHAT_SOCKET_URL ||
      'https://appspot.staging.vchat.vnetwork.dev',
    socketPath: import.meta.env.APP_CHAT_SOCKET_PATH || '/event',
  },
} as const;

export type Env = typeof env;
